
/**
 * The `error_code` values whose `message` may be shown to the user as-is.
 *
 * Mirrors ErrorCodeEnum::isDisplayableMessage() in wad-api and the identical list in
 * wad-platform's useWadError.ts - keep all three in step. Everything else is withheld because
 * most API messages are written for developers, not users: ErrorNotFound alone carries dozens of
 * internal strings and the default ACCESS_FORBIDDEN covers integration-token failures.
 *
 * BAD_DATA and TOO_MANY_ATTEMPTS were always visible here, because their resources set
 * $wrap = null and so were read correctly. They stay in the list: dropping BAD_DATA would replace
 * "The e-mail or password is incorrect" with nothing on the most common login failure.
 */
const displayableErrorCodes = [
  'BAD_DATA',
  'TOO_MANY_ATTEMPTS',
  'MFA_TOKEN_EXPIRED',
  'MFA_NOT_ACTIVE',
  'ACCOUNT_SUSPENDED',
  'ACCOUNT_MOVED',
  'SIGNUP_MOVED',
];

export const useServerErrorStore = defineStore('ServerErrorStore', () => {
  const serverError = ref<ServerError | null>(null);
  const { getErrorMessageTokens, wadGenericError } = useWadError();
  function setServerError(data: unknown) {

    if (data && typeof data === 'object' && 'data' in data && data.data != null) {
      // The API wraps most responses one level down - { data: { success, error_code, message } } -
      // and ofetch hands the whole body over as exception.data, so the fields sit at
      // exception.data.data. Only the resources setting $wrap = null are flat. Reading one level
      // too shallow found undefined on every nested response, which is why a 403 reached the login
      // screen with no message at all: the retired-developer refusal renders as a dead button
      // rather than "This account has moved to wearedevelopers.com."
      const outer = data.data as any;
      const nested = outer?.data;
      // Descend only when the inner object is actually an error body. wad-platform unwraps with a
      // bare `?? `, which would also descend into a flat resource that happens to carry its own
      // `data` key. Every wad-api error resource puts these three fields together, so their
      // presence is what distinguishes the wrapper from a payload.
      const looksLikeError = nested && typeof nested === 'object'
        && ('error_code' in nested || 'message' in nested || 'success' in nested);
      const responseData = looksLikeError ? nested : outer;
      const errorCode = responseData?.error_code || responseData?.error?.code || null;
      const message = responseData?.message || responseData?.error?.message;

      // Withheld messages fall back to the generic sentence rather than to null. Null was the
      // real defect: the form bound straight to this value, so a refusal the API had written a
      // clear explanation for rendered as a button that appeared not to work.
      const displayable = errorCode && displayableErrorCodes.includes(errorCode) && message;

      serverError.value = {
        success: responseData?.success || false,
        error_code: errorCode,
        message: getErrorMessageTokens(displayable ? message : wadGenericError),
      };
    } else {
      // Default error when data is missing
      serverError.value = {
        success: false,
        error_code: null,
        message: 'An error occurred',
      };
    }
  }

  function clearServerError() {
    serverError.value = null;
  }

  return { serverError, setServerError, clearServerError };
});
